
import { useEffect, useState } from "react"
import { ArrowRight, Github, ExternalLink } from "lucide-react"
import { Link } from "react-router-dom"
import "../styles/Home.css"

const Home = () => {
  const [typedText, setTypedText] = useState("")
  const fullText = "I'm a Software Engineer"

  useEffect(() => {
    let index = 0
    const timer = setInterval(() => {
      setTypedText(fullText.substring(0, index))
      index++
      if (index > fullText.length) {
        clearInterval(timer)
      }
    }, 100)

    return () => clearInterval(timer)
  }, [])

  const featuredProjects = [
    {
      id: 1,
      title: "Distributed Task Queue",
      description:
        "A containerized (Docker), micro-service based distributed task queue with a Go backend and Redis and RabbitMQ integrations.",
      tags: ["Fullstack", "Backend", "Go", "Redis", "RabbitMQ", "Docker", "Web/Networking", "Distributed Systems"],
      image: "project_pics/insta-demo-pic.jpg",
      github: "https://github.com/yousefbh1/Project_Demos/blob/main/Distributed%20Task%20Queue/INFO.md",
      demo: "https://github.com/yousefbh1/Project_Demos/blob/main/Distributed%20Task%20Queue/Demo%20Video.md",
    },
    {
      id: 2,
      title: "C++ Thread Library",
      description: "A custom thread management library for C++ that provides comprehensive threading, mutual exlcusion and conditional variable capabilities.",
      tags: ["C++", "Systems", "OS", "Comp. Arch", "Backend"],
      image: "project_pics/thread_lib_pic.jpg",
      demo: "https://github.com/yousefbh1/Project_Demos/blob/main/Thread%20Library/Demo%20Video.md",
      github: "https://github.com/yousefbh1/Project_Demos/blob/main/Thread%20Library/INFO.md",
    },
    {
      id: 3,
      title: "Search Engine",
      description: "A scalable search engine built with Python, SQL, React, and Hadoop that uses a MapReduce pipeline and tf-idf with adjustable PageRank to index and rank hundreds of gigabytes of data in under a second.",
      tags: ["Fullstack", "Backend", "Python", "SQL", "React", "JavaScript", "Web/Networking", "AI/ML", "NoSQL"],
      image: "project_pics/search_eng_pic.png",
      github: "https://github.com/yousefbh1/Project_Demos/blob/main/Search%20Engine/INFO.md",
      demo: "https://github.com/yousefbh1/Project_Demos/blob/main/Search%20Engine/Video.md",
    },
  ]

  return (
    <div className="home-container">
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="greeting">Hello, world!</span>
            <span className="typed-text">
              {typedText}
              <span className="cursor">_</span>
            </span>
          </h1>
          <p className="hero-description">
            Hands-on C++, Python, Go and JavaScript developer with a strong foundation in distributed systems, networking, and web development; proactive open-source contributor building secure, scalable backends and applications.
          </p>
          <div className="hero-buttons">
            <Link to="/projects" className="button button-secondary">
              View Projects <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="button button-primary">
              Contact Me
            </Link>
          </div>
        </div>
        <div className="hero-image">
          <div className="terminal">
            <div className="terminal-header">
              <div className="terminal-buttons">
                <span className="terminal-button close"></span>
                <span className="terminal-button minimize"></span>
                <span className="terminal-button maximize"></span>
              </div>
              <div className="terminal-title">yousef@portfolio ~ </div>
            </div>
            <div className="terminal-body">
              <div className="terminal-line">
                <span className="terminal-prompt">$ </span>
                <span className="terminal-command">whoami</span>
              </div>
              <div className="terminal-output">Software Engineer</div>
              <div className="terminal-line">
                <span className="terminal-prompt">$ </span>
                <span className="terminal-command">ls skills</span>
              </div>
              <div className="terminal-output">C++ C JavaScript Python Go SQL React Docker AWS Linux</div>
              <div className="terminal-line">
                <span className="terminal-prompt">$ </span>
                <span className="terminal-command">cat interests.txt</span>
              </div>
              <div className="terminal-output">Systems Programming, Web Development, Open Source, AI, OS, Algorithms, Tooling, Linux Ricing, Neovim</div>
              <div className="terminal-line">
                <span className="terminal-prompt">$ </span>
                <span className="terminal-command">./welcome.sh</span>
              </div>
              <div className="terminal-output">
                Welcome to my portfolio! Feel free to explore my projects and get in touch.
              </div>
              <div className="terminal-line">
                <span className="terminal-prompt">$ </span>
                <span className="terminal-cursor"></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section">
        <h2 className="section-title">About Me</h2>
        <div className="about-content">
          <p>
            My name is Yousef, and I'm a software engineer with a strong interest in back-end web development, infrastructure, distributed systems, networking, and tooling.
            I'm currently studying <b> Computer Science </b> at the  <b> University of Michigan </b>. I've built everything from multithreaded C++ backends to A.I reinforcement learning agents to real-time collaborative web apps.
          </p>
          <p>
            My focus is on writing efficient code to power large-scale data intensive applications, and empower developers through useful tooling. <br />
            I'm passionate about open source, love solving complex technical problems, and constantly seek to expand my skills through hands-on projects, research, and learning applicable languages. <br />
            <br />
            <b> figuring out how to read the friendly manual. </b> <br />
          </p>

          <Link to="/latest" className="text-link">
            Read more about my latest work <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="featured-projects">
        <div className="section-header">
          <h2 className="section-title">Featured Projects</h2>
          <Link to="/projects" className="view-all">
            View All <ArrowRight size={16} />
          </Link>
        </div>
        <div className="projects-grid">
          {featuredProjects.map((project) => (
            <div key={project.id} className="project-card card">
              <div className="project-image">
                <img src={project.image || "/placeholder.svg"} alt={project.title} />
              </div>
              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                <div className="project-tags">
                  {project.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="project-links">
                  <a href={project.github} className="project-link" target="_blank" rel="noopener noreferrer">
                    <Github size={16} /> Source
                  </a>
                  {project.demo && (
                    <a href={project.demo} className="project-link" target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={16} /> Demo
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Interested in working together?</h2>
          <p className="cta-description">
            I'm always open to discussing new projects, creative ideas or opportunities.
          </p>
          <Link to="/contact" className="button button-primary">
            Get In Touch <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Home
